import { isAfter, isBefore, set } from 'date-fns';

export const to24HourClock = (
  hour12: number,
  period: string | null | undefined,
): number => {
  if (period === 'PM') {
    return hour12 === 12 ? 12 : (hour12 % 12) + 12;
  }
  return hour12 === 12 ? 0 : hour12 % 12;
};

export const getDateFromTime = (
  show12Hours: boolean,
  hours: number | null,
  minutes: number | null,
  seconds: number | null,
  period: string | null,
  referenceDay: Date = new Date(),
) => {
  const hours24 =
    show12Hours && hours != null
      ? to24HourClock(hours, period)
      : (hours as number);

  return set(referenceDay, {
    hours: hours24,
    minutes: minutes ?? 0,
    seconds: seconds ?? 0,
    milliseconds: 0,
  });
};

export const clampDateTimeToBounds = (
  value: Date,
  minTime?: Date,
  maxTime?: Date,
): Date => {
  if (minTime && isBefore(value, minTime)) {
    return minTime;
  }
  if (maxTime && isAfter(value, maxTime)) {
    return maxTime;
  }
  return value;
};

export const isDateTimeWithinBounds = (
  value: Date,
  minTime?: Date,
  maxTime?: Date,
) => {
  if (minTime && isBefore(value, minTime)) {
    return false;
  }
  if (maxTime && isAfter(value, maxTime)) {
    return false;
  }
  return true;
};

export const getSteppedMinutes = (stepMinutes = 5) =>
  [...Array(60).keys()].filter((minute) => minute % stepMinutes === 0);

const getPeriodsForHourCheck = (
  show12Hours: boolean,
  period: string | null,
): Array<string | null> => {
  if (!show12Hours) {
    return [null];
  }
  if (period) {
    return [period];
  }
  return ['AM', 'PM'];
};

const getHourBoundCandidates = (
  day: Date,
  show12Hours: boolean,
  hour: number,
  period: string | null,
  minTime?: Date,
  maxTime?: Date,
  stepMinutes = 5,
) => {
  const periods = getPeriodsForHourCheck(show12Hours, period);

  const candidates: Date[] = [];

  periods.forEach((periodValue) => {
    getSteppedMinutes(stepMinutes).forEach((minute) => {
      candidates.push(
        getDateFromTime(show12Hours, hour, minute, 0, periodValue, day),
      );
    });

    [minTime, maxTime].forEach((bound) => {
      if (!bound) {
        return;
      }
      const boundHour = show12Hours
        ? bound.getHours() % 12 || 12
        : bound.getHours();
      const boundPeriod = bound.getHours() >= 12 ? 'PM' : 'AM';

      if (boundHour === hour && (!show12Hours || periodValue === boundPeriod)) {
        candidates.push(bound);
      }
    });
  });

  return candidates;
};

export const isHourWithinBounds = (
  day: Date,
  show12Hours: boolean,
  hour: number,
  period: string | null,
  minTime?: Date,
  maxTime?: Date,
  stepMinutes = 5,
) =>
  getHourBoundCandidates(
    day,
    show12Hours,
    hour,
    period,
    minTime,
    maxTime,
    stepMinutes,
  ).some((candidate) => isDateTimeWithinBounds(candidate, minTime, maxTime));
